/**
 * Cart Engraving Module
 * Handles adding custom laser engraving to knives from the cart drawer
 */

(function() {
  'use strict';

  // Constants
  const FEE_ONE_LINE = 43781283217459;
  const FEE_TWO_LINES = 43781283250227;
  const MAX_LENGTH = 20;

  // Bonheur Royale always opens on a capital: it is a formal script whose
  // swashes live on the capitals, and a lowercase opening letter reads as a
  // mistake on a blade. Montserrat is left exactly as typed.
  //
  // This modal is the second way engraving reaches an order (the product page
  // block is the first), and it writes 'Engraving Text' straight to the cart, so
  // the rule has to be enforced here too or editing from the cart would bypass
  // it. This select carries the font NAME; the product block keys off the
  // FONT_MAP key instead. Keep both in step with capitaliseForFont() in
  // engraving-admin/app/lib/engraving-geometry.js.
  const AUTO_CAPITAL_FONT_NAMES = { 'Bonheur Royale': true };

  function capitaliseForFont(text, fontName) {
    if (!text || !AUTO_CAPITAL_FONT_NAMES[fontName]) return text;
    const i = text.search(/\S/);
    if (i < 0) return text;
    const up = text[i].toUpperCase();
    // A few letters uppercase to two characters (ß -> SS); growing the string
    // would break MAX_LENGTH and the counter, so leave those alone.
    if (up === text[i] || up.length !== 1) return text;
    return text.slice(0, i) + up + text.slice(i + 1);
  }

  // The engraving text rule the hint states: letters, numbers, spaces and . , ! ? ' -
  // Accented letters are folded to their plain letter first (é to e, ö to o, so José stays Jose
  // instead of losing a letter), then smart punctuation from phone keyboards is mapped back (curly
  // and prime apostrophes to ', curly double quotes to " (not engravable, so it then goes), en and
  // em dashes to -, a no-break space to a space); anything else is dropped, runs of spaces collapse
  // to one and a leading space goes. Keep in step with sanitiseEngravingText() in
  // assets/engraving-option.js.
  const ENGRAVING_ALLOWED_CHAR = /[A-Za-z0-9 .,!?'\-]/;
  function foldEngravingAccents(text) {
    const value = String(text || '');
    return typeof value.normalize === 'function' ? value.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : value;
  }
  function mapEngravingPunctuation(text) {
    return foldEngravingAccents(text)
      .replace(/[\u2018\u2019\u2032]/g, "'")
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/[\u2013\u2014]/g, '-')
      .replace(/\u00A0/g, ' ');
  }
  function sanitiseEngravingText(text) {
    return mapEngravingPunctuation(text)
      .replace(/[^A-Za-z0-9 .,!?'\-]/g, '')
      .replace(/ {2,}/g, ' ')
      .replace(/^ /, '');
  }
  function engravingHasDisallowed(text) {
    return Array.from(mapEngravingPunctuation(text)).some((ch) => !ENGRAVING_ALLOWED_CHAR.test(ch));
  }

  // Screen readers hear why a typed character did not appear; nothing changes visually.
  // The polite region is put in place when the modal opens, so it exists before it speaks.
  let ruleTimer = null;
  function ruleLiveRegion() {
    const panel = document.querySelector('#engraving-modal-dialog .xzc-engrave__panel');
    if (!panel) return null;
    let live = panel.querySelector('[data-engraving-rule-live]');
    if (!live) {
      live = document.createElement('span');
      live.className = 'visually-hidden';
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('data-engraving-rule-live', '');
      panel.appendChild(live);
    }
    return live;
  }
  function announceRule() {
    const live = ruleLiveRegion();
    if (!live) return;
    live.textContent = '';
    clearTimeout(ruleTimer);
    ruleTimer = setTimeout(() => {
      live.textContent = "Only letters, numbers, spaces and . , ! ? ' - can be engraved.";
    }, 60);
  }

  /**
   * Apply the text rule and the font's capital rule to a text box in place. The
   * box is rewritten only when it actually differs, and the caret is put back
   * where it was (less whatever was dropped before it) - otherwise fixing a typo
   * mid-string would throw the cursor to the end on every keystroke. While a
   * keyboard composition is open (Android word suggestions, IMEs) the box is left
   * alone; compositionend applies the rule.
   */
  function enforceFontCase(input, composing) {
    if (!input || composing) return;
    const { fontSelect } = getElements();
    const value = input.value;
    const next = capitaliseForFont(sanitiseEngravingText(value), fontSelect?.value);
    if (next === value) return;
    const focused = document.activeElement === input;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    input.value = next;
    if (focused && start !== null && start !== undefined) {
      const map = (pos) => Math.min(next.length, sanitiseEngravingText(value.slice(0, pos)).length);
      try { input.setSelectionRange(map(start), map(end)); } catch (err) {}
    }
    if (engravingHasDisallowed(value)) announceRule();
  }

  /**
   * Whether the engraving preview feature is switched on for the theme being
   * viewed - the same rule the product-page block applies, so the Plain / Fancy
   * chooser appears in both places or neither. The selected theme comes from
   * the shop metafield, rendered onto the dialog as data-tm-theme-id.
   *
   * Development themes are excepted because their id changes on every
   * `shopify theme dev` run. The theme editor is not: designMode is true there
   * on every theme, which made the setting look like it was being ignored.
   *
   * When it is off the chooser is hidden but the select keeps its value, so the
   * order still records a font; the customer just does not pick it.
   */
  function engravingPreviewEnabled() {
    const dialog = document.getElementById('engraving-modal-dialog');
    const selected = ((dialog && dialog.dataset.tmThemeId) || '').trim();
    const sh = window.Shopify || {};
    const theme = sh.theme || {};
    const active = theme.id != null ? String(theme.id) : '';
    const role = String(theme.role || '').toLowerCase();
    if (role === 'development') return true;
    // The unpublished v2 QA opt-in, the same rule as engravingPreviewAllowed() in
    // assets/engraving-option.js: the chooser shows here wherever the v2 PDP offers it.
    if (role === 'unpublished' && dialog && dialog.dataset.tmPreviewOptIn === 'true') return true;
    return !!(selected && selected === active);
  }

  function syncFontVisibility() {
    const group = document.querySelector('[data-cart-engraving-font]');
    if (group) group.style.display = engravingPreviewEnabled() ? '' : 'none';
  }

  // State
  let currentItemData = null;

  /**
   * Get DOM elements (fresh lookup each time to handle DOM updates)
   */
  function getElements() {
    return {
      dialog: document.getElementById('engraving-modal-dialog'),
      productNameEl: document.getElementById('engraving-modal-product'),
      textInput1: document.getElementById('engraving-text-1'),
      textInput2: document.getElementById('engraving-text-2'),
      counter1: document.getElementById('engraving-counter-1'),
      counter2: document.getElementById('engraving-counter-2'),
      inputGroup2: document.getElementById('engraving-input-group-2'),
      oneLineRadio: document.getElementById('engraving-one-line'),
      twoLinesRadio: document.getElementById('engraving-two-lines'),
      confirmBtn: document.getElementById('engraving-modal-confirm'),
      fontSelect: document.getElementById('engraving-font')
    };
  }

  /**
   * Handle line selection change
   */
  function handleLineSelection() {
    const { inputGroup2, textInput2, twoLinesRadio, counter2 } = getElements();
    
    if (twoLinesRadio?.checked) {
      inputGroup2?.classList.remove('engraving-modal__input-group--hidden');
      textInput2?.focus();
    } else {
      inputGroup2?.classList.add('engraving-modal__input-group--hidden');
      if (textInput2) textInput2.value = '';
      updateCounter(textInput2, counter2);
    }
    validateForm();
  }

  /**
   * Update character counter
   */
  function updateCounter(input, counter) {
    if (!input || !counter) return;
    counter.textContent = `${input.value.length}/${MAX_LENGTH}`;
  }

  /**
   * Validate form and update confirm button state
   */
  function validateForm() {
    const { confirmBtn, textInput1, textInput2, twoLinesRadio } = getElements();
    if (!confirmBtn || !textInput1) return;
    
    const text1 = textInput1.value.trim();
    const text2 = textInput2?.value.trim() || '';
    const isTwoLines = twoLinesRadio?.checked;
    
    const isValid = text1.length > 0 && (!isTwoLines || text2.length > 0);
    confirmBtn.disabled = !isValid;
  }

  /**
   * Open the engraving modal
   */
  function openModal(itemData) {
    const { dialog, productNameEl, textInput1 } = getElements();
    if (!dialog) return;
    
    currentItemData = itemData;

    if (productNameEl) {
      productNameEl.textContent = itemData.productTitle || 'Your Knife';
    }
    const productSeriesEl = document.getElementById('engraving-modal-series');
    if (productSeriesEl) productSeriesEl.textContent = itemData.productSeries || '';

    // Price labels are per knife whenever the line engraves more than one: a set
    // (knife_num > 1) or a line quantity above 1. The confirm handler charges
    // quantity x knifeNum fees, so a plain "+$19" would understate a qty-2 line.
    const units = (itemData.quantity || 1) * (itemData.knifeNum || 1);
    const prices = document.querySelectorAll('.engraving-modal__option-price');
    if (prices[0]) prices[0].textContent = units > 1 ? '+$19/knife' : '+$19';
    if (prices[1]) prices[1].textContent = units > 1 ? '+$29/knife' : '+$29';

    resetForm(itemData.engravingFont);
    clearTimeout(ruleTimer);
    const live = ruleLiveRegion();
    if (live) live.textContent = '';
    syncFontVisibility();
    dialog.showModal();
    lockPage(dialog);
    setTimeout(() => textInput1?.focus(), 100);
  }

  /**
   * Page scroll lock while the dialog is open (same @theme/dialog lock as the drawer,
   * quick-add and notify-me). This is a classic script, so the module loads on demand;
   * the 'close' event covers CANCEL, the close button and Esc.
   */
  let dialogModule = null;
  function withDialogModule(callback) {
    if (dialogModule) return callback(dialogModule);
    import('@theme/dialog').then((module) => { dialogModule = module; callback(module); }).catch(() => {});
  }
  function lockPage(dialog) {
    dialog.setAttribute('scroll-lock', '');
    if (!dialog.__xzcLockBound) {
      dialog.__xzcLockBound = true;
      dialog.addEventListener('close', () => withDialogModule((module) => module.unlockScroll(dialog)));
    }
    withDialogModule((module) => { if (dialog.open) module.lockScroll(dialog); });
  }

  /**
   * Close the engraving modal
   */
  function closeModal() {
    const { dialog, confirmBtn } = getElements();
    if (!dialog) return;
    
    dialog.close();
    currentItemData = null;
    
    if (confirmBtn) {
      confirmBtn.disabled = false;
      confirmBtn.classList.remove('is-loading');
    }
  }

  /**
   * Reset form to initial state. The face starts on the product's own default (the PDP's
   * Plain / Fancy starting value, rendered on the ADD ENGRAVING button by
   * snippets/xz-v2-engraving-font.liquid), so the order records the same font whether the
   * engraving was added on the product page or from the cart.
   * @param {string} [defaultFont] - 'Montserrat' or 'Bonheur Royale'.
   */
  const ENGRAVING_FONT_NAMES = ['Montserrat', 'Bonheur Royale'];
  function resetForm(defaultFont) {
    const { textInput1, textInput2, oneLineRadio, twoLinesRadio, inputGroup2, counter1, counter2, fontSelect } = getElements();

    if (textInput1) textInput1.value = '';
    if (textInput2) textInput2.value = '';
    if (oneLineRadio) oneLineRadio.checked = true;
    if (twoLinesRadio) twoLinesRadio.checked = false;
    if (fontSelect) fontSelect.value = ENGRAVING_FONT_NAMES.indexOf(defaultFont) !== -1 ? defaultFont : 'Montserrat';
    inputGroup2?.classList.add('engraving-modal__input-group--hidden');
    
    updateCounter(textInput1, counter1);
    updateCounter(textInput2, counter2);
    validateForm();
  }

  /**
   * Handle confirm button click
   */
  async function handleConfirm() {
    if (!currentItemData) return;
    
    const { textInput1, textInput2, twoLinesRadio, confirmBtn, fontSelect } = getElements();

    // Already applied as they typed; repeated here because this is the write
    // that reaches the order, and it must never disagree with what they saw.
    const fontName = fontSelect?.value;
    const text1 = capitaliseForFont(sanitiseEngravingText(textInput1?.value || '').trim(), fontName);
    const text2 = twoLinesRadio?.checked
      ? capitaliseForFont(sanitiseEngravingText(textInput2?.value || '').trim(), fontName)
      : '';
    const isTwoLines = twoLinesRadio?.checked && text2.length > 0;
    
    if (!text1) return;
    
    // Disable button to prevent double clicks
    if (confirmBtn) {
      confirmBtn.disabled = true;
      confirmBtn.classList.add('is-loading');
    }
    
    // Store data locally before any async operations
    const { itemKey, variantId, quantity, knifeNum } = currentItemData;
    const lineId = Number(variantId) || variantId;
    const sectionIds = cartSectionIds();
    const withSections = (body) => {
      if (sectionIds.length) {
        body.sections = sectionIds.join(',');
        body.sections_url = window.location.pathname;
      }
      return body;
    };
    let removed = false;
    let originalProperties = {};

    try {
      // The line's own properties, so a failed engraved add can put it back exactly as it was.
      try {
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const original = cartRes.ok ? ((await cartRes.json()).items || []).find((item) => item.key === itemKey) : null;
        if (original && original.properties) originalProperties = original.properties;
      } catch (e) {
        originalProperties = {};
      }

      // Step 1: Remove the original item first (an engraved copy added beside it could exceed the stock).
      const removeRes = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: 0 })
      });
      if (!removeRes.ok) throw new Error('Engraving: removing the original line failed (' + removeRes.status + ')');
      removed = true;

      // Step 2: the engraved knife and its fee (multiplied by the knife count for sets) in ONE request, so the
      // cart never holds an engraved knife without its fee. The request also returns the rendered cart
      // sections (drawer/header or /cart page), so the page can morph as soon as the dialog closes.
      // 'Knife Quantity' only for a set: every reader (fee sync, cart line, cleanup) takes a
      // missing value as 1, and a single knife then carries no extra line property.
      const properties = { 'Engraving Text': text1 };
      if ((knifeNum || 1) > 1) {
        properties['Knife Quantity'] = knifeNum;
      }
      if (isTwoLines) {
        properties['Engraving Text2'] = text2;
      }
      if (fontName) {
        properties['Engraving Font'] = fontName;
      }
      const feeVariantId = isTwoLines ? FEE_TWO_LINES : FEE_ONE_LINE;
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(withSections({
          items: [
            { id: lineId, quantity, properties },
            { id: feeVariantId, quantity: quantity * (knifeNum || 1) }
          ]
        }))
      });
      if (!addRes.ok) throw new Error('Engraving: the engraved add failed (' + addRes.status + ')');
      removed = false;
      let sections = null;
      try {
        const addData = await addRes.json();
        sections = addData && addData.sections ? addData.sections : null;
      } catch (e) {
        sections = null;
      }

      // Close and refresh; the morph replaces the ADD ENGRAVING button, so tell the cart
      // runtime (xz-v2-ws-cart.js) to land focus on the engraved line afterwards.
      const source = currentItemData.sourceEl || null;
      document.dispatchEvent(new CustomEvent('xzc:focus-line', {
        detail: { variantId, source, drawer: !!(source && source.closest && source.closest('.xzc-drawer')) }
      }));
      closeModal();
      refreshCart(sections);

    } catch (error) {
      console.error('Error adding engraving:', error);
      // The original line was removed but the engraved add was refused: put the line
      // back as it was (same variant, quantity and properties) and repaint, so the knife is never lost.
      if (removed) {
        let restored = null;
        try {
          const restoreRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(withSections({ id: lineId, quantity, properties: originalProperties }))
          });
          if (restoreRes.ok) {
            const restoreData = await restoreRes.json().catch(() => null);
            restored = restoreData && restoreData.sections ? restoreData.sections : null;
          }
        } catch (e) {
          restored = null;
        }
        // Over stock Shopify can add part of a refused request (the fee is never capped): fees back in step
        // first; when nothing needed fixing, repaint from the restore's sections.
        const healed = await cleanupOrphanedFees();
        if (!healed) refreshCart(restored, { quiet: true });
      }
      if (confirmBtn) {
        confirmBtn.disabled = false;
        confirmBtn.classList.remove('is-loading');
      }
      alert('Sorry, there was an error adding engraving. Please try again.');
    }
  }

  /**
   * Section ids of the cart-items components on this page (the drawer uses the header
   * section id, the cart page its main-cart section id). Section Rendering caps at 5.
   */
  function cartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const id = el.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids.slice(0, 5);
  }

  /** Visible item count from the rendered sections ([ref="cartItemCount"] excludes fee lines). */
  function visibleCountFrom(sections) {
    if (!sections) return null;
    const parser = new DOMParser();
    for (const id of Object.keys(sections)) {
      if (!sections[id]) continue;
      const node = parser.parseFromString(sections[id], 'text/html').querySelector('[ref="cartItemCount"]');
      const count = node ? parseInt(node.textContent, 10) : NaN;
      if (!isNaN(count)) return count;
    }
    return null;
  }

  /**
   * Refresh the cart UI. With sections from the last write the cart-items components
   * morph immediately; without them they refetch their own section (with their
   * disabled/loading state), so no extra request is made here for the markup.
   */
  async function refreshCart(sections, options) {
    const quiet = !!(options && options.quiet);
    try {
      let itemCount = visibleCountFrom(sections);
      if (itemCount === null) {
        const cartData = await (await fetch('/cart.js')).json();
        // Visible count: hidden engraving-fee lines are not items the shopper sees.
        itemCount = (cartData.items || []).reduce((n, i) => (i.variant_id === FEE_ONE_LINE || i.variant_id === FEE_TWO_LINES ? n : n + i.quantity), 0);
      }
      // quiet: a repaint nobody asked for (the page-load fee heal, a restored line) must not pop the
      // auto-open cart drawer, which opens on every cart:update (cart-drawer.js). The event is dispatched
      // synchronously, so the attribute is only away while the listeners run.
      const drawers = quiet ? Array.prototype.slice.call(document.querySelectorAll('cart-drawer-component[auto-open]')) : [];
      drawers.forEach((drawer) => drawer.removeAttribute('auto-open'));
      try {
        document.dispatchEvent(new CustomEvent('cart:update', {
          detail: { data: { itemCount, sections: sections || {}, source: quiet ? 'cart-engraving-sync' : 'cart-engraving' } }
        }));
      } finally {
        drawers.forEach((drawer) => drawer.setAttribute('auto-open', ''));
      }
    } catch (error) {
      if (!quiet) window.location.reload();
    }
  }

  /**
   * Keep the engraving fees equal to the engraved knives (runs on page load only; real-time
   * changes are handled by component-cart-items.js #updateEngravingFeeIfNeeded, same rule).
   * Each fee variant is set to exactly its required count: surplus fees are trimmed and a
   * missing fee is topped up (an engraved knife without its fee would check out short).
   * Nothing is written when the fees already match; otherwise ONE /cart/update.js with the
   * cart sections, then a quiet repaint (no drawer pop-up). Resolves true when it wrote.
   * Also run by product-form.js after a refused engraved add (window.CartEngraving.cleanup).
   */
  async function cleanupOrphanedFees() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return false;
      const cart = await response.json();
      const items = cart.items || [];

      // Required fees: every line with 'Engraving Text', quantity x 'Knife Quantity' (missing = 1),
      // two-line fee when it also has 'Engraving Text2'.
      let requiredOneLine = 0;
      let requiredTwoLine = 0;
      items.forEach((item) => {
        if (item.properties && item.properties['Engraving Text']) {
          const qty = item.quantity * (parseInt(item.properties['Knife Quantity'], 10) || 1);
          if (item.properties['Engraving Text2']) {
            requiredTwoLine += qty;
          } else {
            requiredOneLine += qty;
          }
        }
      });

      const feeOne = items.find((i) => i.variant_id === FEE_ONE_LINE);
      const feeTwo = items.find((i) => i.variant_id === FEE_TWO_LINES);
      const currentOne = feeOne ? feeOne.quantity : 0;
      const currentTwo = feeTwo ? feeTwo.quantity : 0;

      const updates = {};
      if (currentOne !== requiredOneLine) updates[FEE_ONE_LINE] = requiredOneLine;
      if (currentTwo !== requiredTwoLine) updates[FEE_TWO_LINES] = requiredTwoLine;
      if (Object.keys(updates).length === 0) return false;

      const body = { updates };
      const sectionIds = cartSectionIds();
      if (sectionIds.length) {
        body.sections = sectionIds.join(',');
        body.sections_url = window.location.pathname;
      }
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) return false;
      const data = await res.json().catch(() => null);
      // Totals, fee captions and the count repaint from the returned sections.
      await refreshCart(data && data.sections ? data.sections : null, { quiet: true });
      return true;
    } catch (error) {
      console.error('Error syncing engraving fees:', error);
      return false;
    }
  }

  /**
   * Set up event listeners using delegation
   */
  function setupEventListeners() {
    // Single delegated click handler
    document.addEventListener('click', (e) => {
      const target = e.target;
      
      // Confirm button
      if (target.closest('#engraving-modal-confirm')) {
        e.preventDefault();
        handleConfirm();
        return;
      }
      
      // Close/cancel. Matched on a data attribute rather than ids: the markup
      // and this file are deployed as two separate theme files, and an id
      // rename that lands in only one of them silently breaks the button.
      if (target.closest('[data-cart-engraving-close]') ||
          target.closest('#cart-engraving-modal-close') ||
          target.closest('#engraving-modal-cancel')) {
        closeModal();
        return;
      }
      
      // Backdrop click
      if (target.id === 'engraving-modal-dialog') {
        closeModal();
        return;
      }
      
      // Add Engraving button
      const addBtn = target.closest('.cart-add-engraving-btn');
      if (addBtn) {
        e.preventDefault();
        openModal({
          itemKey: addBtn.dataset.itemKey,
          variantId: addBtn.dataset.variantId,
          quantity: parseInt(addBtn.dataset.quantity, 10) || 1,
          productTitle: addBtn.dataset.productTitle,
          productSeries: addBtn.dataset.productSeries || '',
          knifeNum: parseInt(addBtn.dataset.knifeNum, 10) || 1,
          engravingFont: addBtn.dataset.engravingFont || 'Montserrat',
          sourceEl: addBtn
        });
      }
    });

    // Radio changes
    document.addEventListener('change', (e) => {
      if (e.target.name === 'engraving-lines') {
        handleLineSelection();
        return;
      }
      // Switching to Fancy capitalises text already typed. Switching back to
      // Plain does NOT lower it again - the capital may be the customer's own,
      // and quietly rewriting what they typed is worse than leaving one they
      // did not ask for.
      if (e.target.id === 'engraving-font') {
        const { textInput1, textInput2 } = getElements();
        enforceFontCase(textInput1);
        enforceFontCase(textInput2);
      }
    });

    // Text input changes (and the end of a keyboard composition, which the input
    // handler leaves alone). Counters and the confirm state read the cleaned box.
    const onTextInput = (e) => {
      const { counter1, counter2, textInput1, textInput2 } = getElements();
      const composing = e.type === 'input' && !!e.isComposing;
      if (e.target.id === 'engraving-text-1') {
        enforceFontCase(textInput1, composing);
        updateCounter(textInput1, counter1);
        validateForm();
      } else if (e.target.id === 'engraving-text-2') {
        enforceFontCase(textInput2, composing);
        updateCounter(textInput2, counter2);
        validateForm();
      }
    };
    document.addEventListener('input', onTextInput);
    document.addEventListener('compositionend', onTextInput);

    // A paste is cleaned before the 20-character limit cuts it: the native maxlength would
    // otherwise cut the raw text first, so characters the rule drops (& # and the like) cost
    // letters at the end ('Chef & Dylan’s #1 Knife' left 'Chef Dylan's 1 Kn'). The cleaned text
    // goes in at the caret, trimmed so the whole box stays within the limit, and the input
    // event then runs the usual rule, capital, counter and confirm-state updates.
    document.addEventListener('paste', (e) => {
      const input = e.target;
      if (!input || (input.id !== 'engraving-text-1' && input.id !== 'engraving-text-2')) return;
      const clip = e.clipboardData;
      if (!clip || typeof input.setRangeText !== 'function') return;
      const raw = clip.getData('text/plain');
      e.preventDefault();
      const max = input.maxLength > 0 ? input.maxLength : MAX_LENGTH;
      const value = input.value;
      const start = input.selectionStart == null ? value.length : input.selectionStart;
      const end = input.selectionEnd == null ? start : input.selectionEnd;
      const cleaned = mapEngravingPunctuation(String(raw || '').replace(/[\r\n\t]+/g, ' '))
        .replace(/[^A-Za-z0-9 .,!?'\-]/g, '')
        .replace(/ {2,}/g, ' ');
      const room = Math.max(0, max - (value.length - (end - start)));
      input.setRangeText(cleaned.slice(0, room), start, end, 'end');
      input.dispatchEvent(new Event('input', { bubbles: true }));
      if (engravingHasDisallowed(raw.replace(/[\r\n\t]+/g, ' '))) announceRule();
    });

  }

  // Initialize
  setupEventListeners();
  
  // Run cleanup only on initial page load (safety net)
  // Real-time fee updates are handled by component-cart-items.js
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cleanupOrphanedFees);
  } else {
    cleanupOrphanedFees();
  }

  // Expose for external use
  window.CartEngraving = { open: openModal, close: closeModal, cleanup: cleanupOrphanedFees };
})();
